import { z } from 'zod';
import { clientRequestIdSchema, localDateSchema, objectIdSchema, timezoneSchema, } from './common.js';
/**
 * Energy expenditure.
 *
 * Recorded per *local calendar day* rather than as an instant, because that is
 * the grain every source reports in: a watch gives "active energy for
 * 17 August", not a moment. Storing the day directly avoids converting an
 * instant back into the day it was always meant to describe.
 *
 * Deliberately kept apart from the calorie goal. An adaptive TDEE derived from
 * intake and actual weight change already contains every calorie burned, so
 * subtracting activity on top would count the same training twice. This is
 * context shown beside the intake, not an addition to the budget.
 */
/** Where a record came from. Manual entry today; a device sync later. */
export const activitySourceSchema = z.enum(['manual', 'health_connect', 'import']);
export const activeKcalSchema = z.number().min(0).max(20000);
export const createActivitySchema = z.object({
    /** Defaults to today in the user's timezone. */
    date: localDateSchema.optional(),
    timezone: timezoneSchema.optional(),
    activeKcal: activeKcalSchema,
    /** Steps, when the source reports them. Carried through untouched. */
    steps: z.number().int().min(0).max(500000).nullable().optional(),
    source: activitySourceSchema.default('manual'),
    /**
     * The source's own identifier for this record.
     *
     * Together with `source` this makes a write idempotent: a device re-syncing
     * the same workout updates its record instead of adding a duplicate. Unused
     * by manual entry, which is why it is optional.
     */
    externalId: z.string().trim().min(1).max(200).nullable().optional(),
    label: z.string().trim().max(120).nullable().optional(),
    /** Present when the write came from the offline queue. */
    clientRequestId: clientRequestIdSchema.optional(),
});
/**
 * Replaces everything a given source recorded for one day.
 *
 * The shape a daily device sync actually wants: "as of now, Health Connect says
 * this day was 640 kcal". Re-running it is harmless, and it cannot drift out of
 * step with the device the way incremental appends can. Records from other
 * sources — a manual entry the user made — are left untouched.
 */
export const replaceActivityDaySchema = z.object({
    /**
     * Defaults to today in the user's timezone. Optional on purpose: a phone
     * automation would otherwise have to format a date and get the timezone
     * right, and the server already knows both.
     */
    date: localDateSchema.optional(),
    timezone: timezoneSchema.optional(),
    source: activitySourceSchema,
    entries: z
        .array(z.object({
        activeKcal: activeKcalSchema,
        steps: z.number().int().min(0).max(500000).nullable().optional(),
        externalId: z.string().trim().min(1).max(200).nullable().optional(),
        label: z.string().trim().max(120).nullable().optional(),
    }))
        .max(200),
});
export const activityEntrySchema = z.object({
    id: objectIdSchema,
    date: localDateSchema,
    activeKcal: z.number(),
    steps: z.number().nullable(),
    source: activitySourceSchema,
    label: z.string().nullable(),
    createdAt: z.string(),
});
export const activityDayQuerySchema = z.object({
    date: localDateSchema.optional(),
    timezone: timezoneSchema.optional(),
});
export const activityDaySchema = z.object({
    date: localDateSchema,
    /** Sum across every source for the day. */
    totalKcal: z.number(),
    totalSteps: z.number().nullable(),
    entries: z.array(activityEntrySchema),
});
export const activityRangeQuerySchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    timezone: timezoneSchema.optional(),
});
export const activityDayTotalSchema = z.object({
    date: localDateSchema,
    activeKcal: z.number(),
    steps: z.number().nullable(),
});
//# sourceMappingURL=activity.js.map